import { useState } from 'react';
import {
  EmptyState,
  InfoCard,
  Progress,
  ResponseErrorPanel,
  WarningPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import type { ClusterObjects } from '@backstage/plugin-kubernetes-common';
import {
  ClusterContext,
  PodMetricsContext,
  useKubernetesObjects,
} from '@backstage/plugin-kubernetes-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Typography from '@material-ui/core/Typography';
import {
  defaultCluster,
  describeFetchError,
  orderClusters,
  podsOf,
  summarize,
} from './lib';
import { PodTable } from './PodTable';

const ClusterPods = ({
  clusterObjects,
}: {
  clusterObjects: ClusterObjects;
}) => {
  const { cluster, errors, podMetrics } = clusterObjects;
  const pods = podsOf(clusterObjects);
  const { total, running, withErrors } = summarize(pods);
  const metrics = new Map([[cluster.name, podMetrics]]);

  return (
    <>
      {errors.map(error => (
        <Box key={describeFetchError(error)} mb={2}>
          <WarningPanel
            severity="error"
            title={`Could not read ${cluster.title ?? cluster.name}`}
            message={describeFetchError(error)}
            defaultExpanded
          />
        </Box>
      ))}
      {pods.length === 0 ? (
        <Typography color="textSecondary">
          No pods on {cluster.name} for this service.
        </Typography>
      ) : (
        <ClusterContext.Provider value={cluster}>
          <PodMetricsContext.Provider value={metrics}>
            <Box display="flex" gridGap={24} mb={1}>
              <Typography variant="body2">
                <b>{total}</b> pods
              </Typography>
              <Typography variant="body2">
                <b>{running}</b> running
              </Typography>
              <Typography
                variant="body2"
                color={withErrors ? 'error' : 'textPrimary'}
              >
                <b>{withErrors}</b> with errors
              </Typography>
            </Box>
            <PodTable pods={pods} />
          </PodMetricsContext.Provider>
        </ClusterContext.Provider>
      )}
    </>
  );
};

export const EntityKubernetesPods = () => {
  const { entity } = useEntity();
  const { kubernetesObjects, loading, error } = useKubernetesObjects(entity);
  const [selected, setSelected] = useState<string>();

  if (error) {
    return <ResponseErrorPanel error={new Error(error)} />;
  }
  if (loading && !kubernetesObjects) {
    return <Progress />;
  }

  const clusters = orderClusters(kubernetesObjects?.items ?? []);
  const activeName = selected ?? defaultCluster(clusters);
  const active = clusters.find(c => c.cluster.name === activeName);

  return (
    <InfoCard
      title="Kubernetes"
      subheader={active?.cluster.title ?? active?.cluster.name}
      action={
        <ButtonGroup size="small" aria-label="cluster">
          {clusters.map(({ cluster }) => (
            <Button
              key={cluster.name}
              color="primary"
              variant={cluster.name === activeName ? 'contained' : 'outlined'}
              aria-pressed={cluster.name === activeName}
              onClick={() => setSelected(cluster.name)}
            >
              {cluster.name}
            </Button>
          ))}
        </ButtonGroup>
      }
    >
      {active ? (
        <ClusterPods clusterObjects={active} />
      ) : (
        <EmptyState
          title="No clusters configured"
          missing="info"
          description="The Kubernetes plugin has no clusters to read from."
        />
      )}
    </InfoCard>
  );
};
